import React, { useState, useEffect } from "react";

import { Avatar, Box, Tooltip, Checkbox, Alert, Typography, Divider, Button } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import axios from "axios";
import apiUrl from "../api";
import PopUpWindow from "../popUpWindow/PopUpWindow";
import Loader from "../loader/Loader";
import ViewTransaction from "../viewTransaction/ViewTransaction";
import io from "socket.io-client";
import Select, { components } from "react-select"; // Import components from react-select

// Add CustomOption component for tooltips with property names
const CustomOption = (props) => {
  return (
    <Tooltip
      title={props.data.tooltip || props.data.label}
      placement="bottom"
      arrow
    >
      <div>
        <components.Option {...props} />
      </div>
    </Tooltip>
  );
};

function formatDate(date) {
  date = new Date(date);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

const getColor = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const color = `hsl(${hash % 360}, 100%, 30%)`;
  return color;
};
const socket = io(`${apiUrl}/`);

const Profile = (props) => {
  const [user, setUser] = useState(props.user);
  const me = JSON.parse(localStorage.getItem("user"));
  const [newPassword, setNewPassword] = useState("");
  const permissionsArray = Object.keys(user.permissions);
  const itemsPerColumn = 5;
  const numColumns = Math.ceil(permissionsArray.length / itemsPerColumn);
  const [isLoaded, setIsLoaded] = useState(false);
  // State to manage password visibility
  const [showPassword, setShowPassword] = useState(false);

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [timelineItems, setTimelineItems] = useState([]);
  const [timelineTotal, setTimelineTotal] = useState(0);
  const [timelinePage, setTimelinePage] = useState(1);

  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedTimeline, setSelectedTimeline] = useState(null);
  const [transactionIsLoading, setTransactionIsLoading] = useState(false);
  const [transactionPopupOpen, setTransactionPopupOpen] = useState(false);
  
  // New state for property assignments
  const [properties, setProperties] = useState([]);
  const [selectedPropertiesForCleaning, setSelectedPropertiesForCleaning] = useState([]);
  const [selectedPropertiesForPool, setSelectedPropertiesForPool] = useState([]);
  const [assignmentSuccess, setAssignmentSuccess] = useState("");
  const [assignmentError, setAssignmentError] = useState("");
  const [isAssigning, setIsAssigning] = useState(false);

  const handleSave = async () => {
    const response = await axios.put(
      `${apiUrl}/auth/update/${user._id}`,
      {
        username: user.username,
        name: user.name,
        email: user.email,
        phone: user.phone,
        tax_id: user.tax_id,
        amka: user.amka,
        iban: user.iban,
        permissions: user.permissions,
        newPassword: newPassword,
      },
      {
        headers: {
          Authorization: `Bearer ${me.token}`,
        },
      }
    );

    setUser({
      ...user,
      permissions: response.data.user.permissions,
    });
    setSuccess("Profile updated successfully");

    try {
    } catch (error) {
      setError("Error updating profile");
    }
  };

  const fetchTransaction = async (transactionId) => {
    try {
      setTransactionIsLoading(true);
      const response = await axios.get(
        `${apiUrl}/transaction/${transactionId}`,
        {
          headers: {
            Authorization: `Bearer ${me.token}`,
          },
        }
      );

      const transaction = response.data.transaction;
      setSelectedTransaction({
        _id: transaction._id,
        date: formatDate(transaction.transaction_date),
        amount: transaction.amount,
        currency: transaction.currency,
        reason: transaction.reason,
        payment_method: transaction.payment_method,
        status: transaction.transaction_status,
        receiver: transaction.is_receiver
          ? transaction.user
          : transaction.company
          ? transaction.company
          : "",
        sender: transaction.is_receiver
          ? transaction.company
            ? transaction.company
            : transaction.is_payment
            ? transaction.user
            : ""
          : transaction.user,
        property: transaction.property,
        notes: transaction.notes,
        is_payment: transaction.is_payment,
        attachments: transaction.attachments,
        created_at: transaction.created_at,
        updated_at: transaction.updated_at,
        approved_by: transaction.approved_by,
        rejected_by: transaction.rejected_by,
        approved_at: transaction.approved_at,
        rejected_at: transaction.rejected_at,
      });

      setTransactionIsLoading(false);
    } catch (error) {
      setError("Error fetching transaction");
    }
  };
  
  // Add new function to fetch properties with assignment info
  const fetchAssignableProperties = async () => {
    try {
      console.log("Fetching properties for assignment...");
      const response = await axios.get(`${apiUrl}/property/assignments`, {
        headers: {
          Authorization: `Bearer ${me.token}`,
        },
      });
      console.log("Fetched assignable properties:", response.data.properties);
      
      // Transform the data for the react-select component
      const formattedProperties = response.data.properties.map(property => ({
        value: property._id,
        label: property.name,
        isCleaningAssignee: property.isCleaningAssignee,
        isPoolAssignee: property.isPoolAssignee
      }));
      
      setProperties(formattedProperties);
    } catch (error) {
      console.error("Failed to fetch properties for assignment:", error);
      setError("Failed to fetch properties for assignment.");
    }
  };
  
  // Handle cleaning property selection with debug logging
  const handleCleaningPropertyChange = (selectedOptions) => {
    console.log("Selected cleaning properties:", selectedOptions);
    setSelectedPropertiesForCleaning(selectedOptions || []);
  };
  
  // Handle pool property selection with debug logging
  const handlePoolPropertyChange = (selectedOptions) => {
    console.log("Selected pool properties:", selectedOptions);
    setSelectedPropertiesForPool(selectedOptions || []);
  };
  
  // Add functions to handle property assignments with improved error handling
  const handleCleaningAssignment = async () => {
    if (selectedPropertiesForCleaning.length === 0) {
      setAssignmentError("Please select at least one property for cleaning assignment.");
      return;
    }

    setIsAssigning(true);
    setAssignmentError("");
    setAssignmentSuccess("");

    try {
      // Extract just the property IDs
      const propertyIds = selectedPropertiesForCleaning.map(p => p.value);
      
      console.log("Starting cleaning assignments for properties:", propertyIds);
      
      // Use the new bulk endpoint
      const response = await axios.post(
        `${apiUrl}/property/cleaning-assignments`,
        {
          propertyIds,
          action: 'assign'
        },
        {
          headers: {
            Authorization: `Bearer ${me.token}`
          }
        }
      );
      
      console.log("Cleaning assignment results:", response.data);
      
      setAssignmentSuccess(`Successfully assigned you as cleaning assignee to ${selectedPropertiesForCleaning.length} properties.`);
      
      // Reset selected properties
      setSelectedPropertiesForCleaning([]);
      
      // Refresh properties list
      fetchAssignableProperties();
    } catch (error) {
      console.error("Failed to assign cleaning duties:", error);
      setAssignmentError(`Failed to assign cleaning duties: ${error.message || "Please try again."}`);
    }
    setIsAssigning(false);
  };

  const handlePoolAssignment = async () => {
    if (selectedPropertiesForPool.length === 0) {
      setAssignmentError("Please select at least one property for pool maintenance assignment.");
      return;
    }

    setIsAssigning(true);
    setAssignmentError("");
    setAssignmentSuccess("");

    try {
      // Extract just the property IDs
      const propertyIds = selectedPropertiesForPool.map(p => p.value);
      
      console.log("Starting pool assignments for properties:", propertyIds);
      
      // Use the new bulk endpoint
      const response = await axios.post(
        `${apiUrl}/property/pool-assignments`,
        {
          propertyIds,
          action: 'assign'
        },
        {
          headers: {
            Authorization: `Bearer ${me.token}`
          }
        }
      );
      
      console.log("Pool assignment results:", response.data);
      
      setAssignmentSuccess(`Successfully assigned you as pool maintenance assignee to ${selectedPropertiesForPool.length} properties.`);
      
      // Reset selected properties
      setSelectedPropertiesForPool([]);
      
      // Refresh properties list
      fetchAssignableProperties();
    } catch (error) {
      console.error("Failed to assign pool maintenance duties:", error);
      setAssignmentError(`Failed to assign pool maintenance duties: ${error.message || "Please try again."}`);
    }
    setIsAssigning(false);
  };

  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        setIsLoaded(true);
        const response = await axios.get(
          `${apiUrl}/timeline/getUserTimelines/${user._id}?page=${timelinePage}`,
          {
            headers: {
              Authorization: `Bearer ${me.token}`,
            },
          }
        );
        setTimelineItems((prevItems) => [
          ...prevItems,
          ...response.data.timelines,
        ]);
        setTimelineTotal(response.data.totalItems);
      } catch (error) {
        setError("Error fetching timeline");
      }
    };
    fetchTimeline();
    
    // Use the new function instead
    console.log("Component mounted, fetching assignable properties...");
    fetchAssignableProperties();
  }, [timelinePage]);

  useEffect(() => {
    const handleUpdateUserBalance = (data) => {

      if (data.user_id === user._id) {
        setUser((prevUser) => {
          return { ...prevUser, balance: data.new_balance };
        });
      }
    };

    socket.on("updateUserBalance", handleUpdateUserBalance);

    return () => {
      socket.off("updateUserBalance", handleUpdateUserBalance);
    };
  }, [user._id]);

  // Log current state for debugging
  useEffect(() => {
    console.log("Current state - Properties:", properties);
    console.log("Current state - Selected cleaning properties:", selectedPropertiesForCleaning);
    console.log("Current state - Selected pool properties:", selectedPropertiesForPool);
  }, [properties, selectedPropertiesForCleaning, selectedPropertiesForPool]);

  return (
    <div className="profile__container">
      {transactionPopupOpen && (
        <PopUpWindow
          onClose={() => setTransactionPopupOpen(false)}
          unfocusClose={true}
        >
          {transactionIsLoading ? (
            <Loader />
          ) : (
            <ViewTransaction transaction={selectedTransaction} />
          )}
        </PopUpWindow>
      )}
{/* 
      <div
        className="profile__balance"
        style={{
          color: user.balance <= 0 ? "green" : "red",
          display: "flex",
        }}
      >
        <h2>Balance: {user.balance}€</h2>
      </div> */}

      <div className="profile__avatar">
        <Avatar
          style={{
            backgroundColor: getColor(user.name),
            width: "50px",
            height: "50px",
            fontSize: "25px",
          }}
        >
          {user.name.charAt(0).toUpperCase()}
        </Avatar>
      </div>
      <div className="profile__info">
        <div className="form-group">
          <div className="form-field">
            <label className="form-field-label" htmlFor="fullName">
              Full Name:
            </label>
            <input
              className="form-field-input"
              type="text"
              id="fullName"
              name="fullName"
              value={user.name}
              autoComplete="off" // Added here
              onChange={(e) => {
                setUser({ ...user, name: e.target.value });
                console.log(user.name);
              }}
            />
          </div>

          <div className="form-field">
            <label className="form-field-label" htmlFor="username">
              Username:
            </label>

            <input
              className="form-field-input"
              type="text"
              id="username"
              name="username"
              value={user.username}
              autoComplete="off" // Added here
              onChange={(e) => setUser({ ...user, username: e.target.value })}
            />
          </div>
        </div>
        <div className="form-group">
          <div className="form-field">
            <label className="form-field-label" htmlFor="email">
              Email:
            </label>
            <input
              className="form-field-input"
              type="email"
              id="email"
              name="email"
              value={user.email}
              autoComplete="off" // Added here
              onChange={(e) => setUser({ ...user, email: e.target.value })}
            />
          </div>

          <div className="form-field">
            <label className="form-field-label" htmlFor="phone">
              Phone:
            </label>
            <input
              className="form-field-input"
              type="text"
              id="phone"
              name="phone"
              value={user.phone}
              autoComplete="off" // Added here
              onChange={(e) => setUser({ ...user, phone: e.target.value })}
            />
          </div>
        </div>

        <div className="form-group">
          <div className="form-field">
            <label className="form-field-label" htmlFor="taxId">
              Tax ID:
            </label>
            <input
              className="form-field-input"
              type="text"
              id="taxId"
              name="taxId"
              value={user.tax_id}
              autoComplete="off" // Added here
              onChange={(e) => setUser({ ...user, tax_id: e.target.value })}
            />
          </div>

          <div className="form-field">
            <label className="form-field-label" htmlFor="amka">
              AMKA:
            </label>
            <input
              className="form-field-input"
              type="text"
              id="amka"
              name="amka"
              value={user.amka}
              autoComplete="off" // Added here
              onChange={(e) => setUser({ ...user, amka: e.target.value })}
            />
          </div>

          <div className="form-field">
            <label className="form-field-label" htmlFor="iban">
              IBAN:
            </label>
            <input
              className="form-field-input"
              type="text"
              id="iban"
              name="iban"
              autoComplete="off" // Already present
              value={user.iban}
              onChange={(e) => setUser({ ...user, iban: e.target.value })}
            />
          </div>
        </div>
        {me.permissions["canChangePermissions"] && (
          <>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "20px",
                width: "100%",
              }}
            >
              <hr
                style={{
                  color: "var(--primary-color)",
                  backgroundColor: "var(--primary-color)",
                  height: 1,
                  width: "100%",
                }}
              />
              <h4
                style={{
                  color: "var(--primary-color)",
                  fontSize: "20px",
                  fontWeight: "600",
                  whiteSpace: "nowrap",
                  margin: "0 5px",
                }}
              >
                Admin Fields
              </h4>

              <hr
                style={{
                  color: "var(--primary-color)",
                  backgroundColor: "var(--primary-color)",
                  height: 1,
                  width: "100%",
                }}
              />
            </div>

            <div className="form-group">
              <div className="form-field">
                <label className="form-field-label" htmlFor="reset">
                  Reset Password:
                </label>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <input
                    className="form-field-input"
                    type={showPassword ? "text" : "password"}
                    id="reset"
                    name="reset"
                    autoComplete="off" // Already present
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <Tooltip
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <VisibilityOff
                        onClick={() => setShowPassword(false)}
                        style={{ color: "var(--primary-color)" }}
                      />
                    ) : (
                      <Visibility
                        onClick={() => {
                          setShowPassword(true);
                        }}
                        style={{ color: "var(--primary-color)" }}
                      />
                    )}
                  </Tooltip>
                </div>
              </div>
            </div>

            <h4 className="form-field-label">Permissions:</h4>

            <div className="permissions-container">
              {[...Array(numColumns)].map((_, columnIndex) => (
                <div key={columnIndex} className="permissions-column">
                  {permissionsArray
                    .slice(
                      columnIndex * itemsPerColumn,
                      (columnIndex + 1) * itemsPerColumn
                    )
                    .map((permission, index) => (
                      <div key={index} className="permission">
                        <Checkbox
                          checked={user.permissions[permission]}
                          onChange={() => {
                            setUser({
                              ...user,
                              permissions: {
                                ...user.permissions,
                                [permission]: !user.permissions[permission],
                              },
                            });
                          }}
                        />
                        <span>{permission}</span>
                      </div>
                    ))}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Add Property Assignment Section */}
        {/* Check if user has appropriate permissions */}
      

        <div className="update-profile-button">
          <button
            className="update-profile-button__button"
            style={{
              backgroundColor: "var(--primary-color)",
              color: "var(--primary-text-color)",
              fontSize: "15px",
              fontWeight: "600",
              width: "100%",
            }}
            onClick={() => {
              handleSave();
            }}
          >
            <span>Save</span>
          </button>
        </div>
        {/* <div
          className="timeline"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "90%",
            marginTop: "20px",
          }}
        >
          <h2>Timeline</h2>
          <div
            className="timeline-items"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              alignContent: "center",
              width: "100%",
              overflowY: "auto",
              backgroundColor: "#fff",
              padding: "10px",
              borderRadius: "5px",
            }}
          >
            {timelineItems.map((item) => (
              <div
                key={item._id}
                className="timeline-item"
                onClick={async () => {
                  setSelectedTransaction(null);
                  await fetchTransaction(item.type_id);
                  setTransactionPopupOpen(true);
                }}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                  width: "100%",
                  padding: "10px",
                  margin: "5px",
                  borderRadius: "5px",
                  backgroundColor: "var(--tertiary-color)",
                  cursor: "pointer",
                }}
              >
                <p>
                  {item.to_user === user._id
                    ? item.to_message
                    : item.from_message}
                </p>
                <p
                  style={{
                    fontSize: "12px",
                    color: "var(--secondary-text-color)",
                  }}
                >
                  {new Date(item.created_at).toLocaleDateString()} -{" "}
                  {new Date(item.created_at).toLocaleTimeString()}
                </p>
              </div>
            ))}
          </div>
          {timelineItems.length < timelineTotal && (
            <button
              onClick={() => setTimelinePage(timelinePage + 1)}
              style={{
                backgroundColor: "var(--primary-color)",
                color: "var(--primary-text-color)",
                fontSize: "15px",
                fontWeight: "600",
                width: "50%",
              }}
            >
              Load more
            </button>
          )}
        </div> */}
      </div>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}
    </div>
  );
};

export default Profile;
